/**
 * V3.4: Drag-and-drop pivot builder component
 */

import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	KeyboardSensor,
	MouseSensor,
	TouchSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useCallback, useMemo, useState } from 'react'
import type { AxisField, ColumnField, GroupBySource } from '../../types'
import { DndAnnouncer } from '../DndAnnouncer'
import { DraggableField, type FieldItem } from './DraggableField'
import { DropZone } from './DropZone'

/**
 * Pure helper: returns true when adding draggedFieldId to columnFields would exceed the 2-column cap.
 * Excludes draggedFieldId from the count if it is already in columnFields (re-dropping in the same zone).
 * Exported for unit testing.
 */
export function isColumnZoneFull(columnFields: ColumnField[], draggedFieldId: string): boolean {
	return columnFields.filter((f) => f.field !== draggedFieldId).length >= 2
}

interface AvailableField {
	id: string
	label: string
	source: GroupBySource
}

interface PivotBuilderProps {
	availableFields: AvailableField[]
	rowFields: AxisField[]
	columnFields: ColumnField[]
	onRowFieldsChange: (fields: AxisField[]) => void
	onColumnFieldsChange: (fields: ColumnField[]) => void
}

type ZoneType = 'available' | 'rows' | 'columns'

export function PivotBuilder({
	availableFields,
	rowFields,
	columnFields,
	onRowFieldsChange,
	onColumnFieldsChange,
}: PivotBuilderProps) {
	const [activeId, setActiveId] = useState<string | null>(null)
	const [announcement, setAnnouncement] = useState('')

	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
		useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	// Convert to FieldItem format
	const availableItems: FieldItem[] = useMemo(() => {
		const usedFieldIds = new Set([...rowFields.map((f) => f.field), ...columnFields.map((f) => f.field)])
		return availableFields
			.filter((f) => !usedFieldIds.has(f.id))
			.map((f) => ({
				id: `available-${f.id}`,
				field: f.id,
				source: f.source,
				label: f.label,
				type: 'available' as const,
			}))
	}, [availableFields, rowFields, columnFields])

	const rowItems: FieldItem[] = useMemo(
		() =>
			rowFields.map((f) => ({
				id: `row-${f.field}`,
				field: f.field,
				source: f.source,
				label: availableFields.find((af) => af.id === f.field)?.label || f.field.replace('custom.', ''),
				type: 'row' as const,
			})),
		[rowFields, availableFields],
	)

	const columnItems: FieldItem[] = useMemo(
		() =>
			columnFields.map((f) => ({
				id: `column-${f.field}`,
				field: f.field,
				source: f.source,
				label: availableFields.find((af) => af.id === f.field)?.label || f.field.replace('custom.', ''),
				type: 'column' as const,
			})),
		[columnFields, availableFields],
	)

	const allItems = useMemo(
		() => [...availableItems, ...rowItems, ...columnItems],
		[availableItems, rowItems, columnItems],
	)

	const activeItem = activeId ? allItems.find((item) => item.id === activeId) : null

	const findZone = useCallback((itemId: string): ZoneType => {
		if (itemId.startsWith('available-')) return 'available'
		if (itemId.startsWith('row-')) return 'rows'
		if (itemId.startsWith('column-')) return 'columns'
		// Check zone ids
		if (itemId === 'available' || itemId === 'rows' || itemId === 'columns') {
			return itemId as ZoneType
		}
		return 'available'
	}, [])

	const handleDragStart = useCallback((event: DragStartEvent) => {
		setActiveId(event.active.id as string)
	}, [])

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			setActiveId(null)

			if (!over) return

			const draggedItem = allItems.find((item) => item.id === active.id)
			if (!draggedItem) return

			const targetZone = findZone(over.id as string)
			const sourceZone = findZone(active.id as string)

			const zoneLabel =
				targetZone === 'rows' ? 'Row Fields' : targetZone === 'columns' ? 'Column Fields' : 'Available Fields'
			const sourceLabel = draggedItem.source === 'system_field' ? 'system' : 'custom'

			if (sourceZone === targetZone) {
				if (sourceZone === 'rows' && active.id !== over.id) {
					const oldIndex = rowItems.findIndex((i) => i.id === active.id)
					const newIndex = rowItems.findIndex((i) => i.id === over.id)
					if (oldIndex !== -1 && newIndex !== -1) {
						const reordered = arrayMove(rowFields, oldIndex, newIndex).map((f, i) => ({
							...f,
							order: i,
						}))
						onRowFieldsChange(reordered)
						setAnnouncement(`${draggedItem.label} (${sourceLabel}) reordered in ${zoneLabel}`)
					}
				} else if (sourceZone === 'columns' && active.id !== over.id) {
					const oldIndex = columnItems.findIndex((i) => i.id === active.id)
					const newIndex = columnItems.findIndex((i) => i.id === over.id)
					if (oldIndex !== -1 && newIndex !== -1) {
						const reordered = arrayMove(columnFields, oldIndex, newIndex).map((f, i) => ({
							...f,
							order: i,
						}))
						onColumnFieldsChange(reordered)
						setAnnouncement(`${draggedItem.label} (${sourceLabel}) reordered in ${zoneLabel}`)
					}
				}
				return
			}

			if (targetZone === 'columns' && isColumnZoneFull(columnFields, draggedItem.field)) {
				setAnnouncement('Maximum two column fields supported. Remove one first.')
				return
			}

			if (sourceZone === 'rows') {
				onRowFieldsChange(rowFields.filter((f) => f.field !== draggedItem.field).map((f, i) => ({ ...f, order: i })))
			} else if (sourceZone === 'columns') {
				onColumnFieldsChange(
					columnFields.filter((f) => f.field !== draggedItem.field).map((f, i) => ({ ...f, order: i })),
				)
			}

			if (targetZone === 'rows') {
				const filtered = rowFields.filter((f) => f.field !== draggedItem.field)
				const newField: AxisField = {
					field: draggedItem.field,
					source: draggedItem.source,
					order: filtered.length,
				}
				onRowFieldsChange([...filtered, newField])
			} else if (targetZone === 'columns') {
				const filtered = columnFields.filter((f) => f.field !== draggedItem.field)
				const newField: ColumnField = {
					field: draggedItem.field,
					source: draggedItem.source,
					order: filtered.length,
				}
				onColumnFieldsChange([...filtered, newField])
			}

			setAnnouncement(`${draggedItem.label} (${sourceLabel}) moved to ${zoneLabel}`)
		},
		[allItems, findZone, rowFields, columnFields, rowItems, columnItems, onRowFieldsChange, onColumnFieldsChange],
	)

	const handleRemoveRow = useCallback(
		(itemId: string) => {
			const field = itemId.replace('row-', '')
			onRowFieldsChange(rowFields.filter((f) => f.field !== field).map((f, i) => ({ ...f, order: i })))
		},
		[rowFields, onRowFieldsChange],
	)

	const handleRemoveColumn = useCallback(
		(itemId: string) => {
			const field = itemId.replace('column-', '')
			onColumnFieldsChange(columnFields.filter((f) => f.field !== field).map((f, i) => ({ ...f, order: i })))
		},
		[columnFields, onColumnFieldsChange],
	)

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}>
			<div className='space-y-4' data-testid='pivot-builder'>
				{/* Available Fields */}
				<DropZone
					id='available'
					testId='available-fields'
					title='Available Fields'
					items={availableItems}
					emptyText='All fields in use'
					className='bg-white'
				/>

				<div className='grid grid-cols-2 gap-4'>
					{/* Row Fields */}
					<DropZone
						id='rows'
						testId='row-fields-zone'
						title='Row Fields'
						items={rowItems}
						onRemoveItem={handleRemoveRow}
						emptyText='Drag fields here for rows'
					/>

					{/* Column Fields */}
					<DropZone
						id='columns'
						testId='column-fields-zone'
						title={`Column Fields${columnItems.length >= 2 ? ' (max 2)' : ''}`}
						items={columnItems}
						onRemoveItem={handleRemoveColumn}
						emptyText='Drag up to 2 fields here for columns'
					/>
				</div>
			</div>

			<DragOverlay>{activeItem ? <DraggableField item={activeItem} /> : null}</DragOverlay>
			<DndAnnouncer message={announcement} />
		</DndContext>
	)
}
